import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import api from "../../api/axios";
import endpoints from "../../api/endpoints";
import { extractApiErrorMessage } from "../../utils/apiError";

interface AdminProfile {
  isAdmin?: boolean;
  admin?: boolean;
  roles?: string[];
  groupName?: string;
  nuid?: string;
  [key: string]: unknown;
}

export interface ProfileState {
  profile: Record<string, unknown> | null;
  adminProfile: AdminProfile | null;
  isAdmin: boolean;
  loading: boolean;
  error: string | null;
  profileError: string | null;
  policyError: string | null;
  policyUrl: string | null;
}

const initialState: ProfileState = {
  profile: null,
  adminProfile: null,
  isAdmin: false,
  loading: false,
  error: null,
  profileError: null,
  policyError: null,
  policyUrl: null,
};

export const fetchProfile = createAsyncThunk(
  "profile/fetchProfile",
  async (_arg, { rejectWithValue, signal }) => {
    try {
      const options: any = { signal };
      const url = endpoints.login?.profile ?? "/profileV2";
      const response = await api.get(url, options);
      if (response.data?.success === false) {
        return rejectWithValue(
          extractApiErrorMessage({ response }, "Failed to fetch profile"),
        );
      }
      return response.data;
    } catch (error: any) {
      return rejectWithValue(
        extractApiErrorMessage(error, "Failed to fetch profile"),
      );
    }
  },
  {
    condition: (_arg, { getState }) => {
      const profileState = (getState() as { profile?: ProfileState }).profile;
      return Boolean(
        profileState &&
          !profileState.profile &&
          !profileState.loading &&
          !profileState.profileError,
      );
    },
  },
);

export const fetchPolicy = createAsyncThunk(
  "profile/fetchPolicy",
  async (_arg, { rejectWithValue, signal }) => {
    try {
      const options: any = { signal };
      const url = endpoints.login?.policy ?? "/policy";
      const response = await api.get(url, options);
      if (response.data?.success === false) {
        return rejectWithValue(
          extractApiErrorMessage({ response }, "Failed to fetch policy"),
        );
      }
      return response.data;
    } catch (error: any) {
      if (
        error?.message === "canceled" ||
        error?.message === "Request cancelled"
      ) {
        return rejectWithValue("Request cancelled");
      }

      return rejectWithValue(
        extractApiErrorMessage(error, "Failed to fetch policy"),
      );
    }
  },
);

export const fetchAdminProfile = createAsyncThunk(
  "profile/fetchAdminProfile",
  async (_arg, { rejectWithValue, signal }) => {
    try {
      const options: any = { signal };
      const url = endpoints.login?.adminProfile ?? "/profile/admin-status";
      const response = await api.get(url, options);
      return response.data;
    } catch (error: any) {
      if (
        error?.message === "canceled" ||
        error?.message === "Request cancelled"
      ) {
        console.log("Admin profile request exception");
        return rejectWithValue("Request cancelled");
      }
      const message =
        error?.response?.data?.message ||
        error?.message ||
        "Failed to fetch admin profile";
      return rejectWithValue(message);
    }
  },
);

const slice = createSlice({
  name: "profile",
  initialState,
  reducers: {},
  extraReducers: (builder) => {
    builder.addCase(fetchProfile.pending, (state) => {
      state.loading = true;
      state.profileError = null;
    });
    builder.addCase(fetchProfile.fulfilled, (state, action) => {
      state.loading = false;
      state.profile = action.payload as Record<string, unknown>;
    });
    builder.addCase(fetchProfile.rejected, (state, action) => {
      state.loading = false;
      state.profileError = action.payload as string;
    });
    builder.addCase(fetchPolicy.pending, (state) => {
      state.loading = true;
      state.policyError = null;
    });
    builder.addCase(fetchPolicy.fulfilled, (state, action) => {
      state.loading = false;
      state.policyUrl = action.payload.policyUrl;
    });
    builder.addCase(fetchPolicy.rejected, (state, action) => {
      state.loading = false;
      state.policyError = action.payload as string;
    });
    builder.addCase(fetchAdminProfile.pending, (state) => {
      state.loading = true;
      state.error = null;
    });
    builder.addCase(fetchAdminProfile.fulfilled, (state, action) => {
      state.loading = false;
      state.adminProfile = action.payload as AdminProfile;
      // Attempt to derive isAdmin flag from response using safe guards
      let derivedIsAdmin = false;
      const payload = action.payload as AdminProfile;
      if (payload && typeof payload === "object") {
        const obj = payload;
        derivedIsAdmin = Boolean(
          obj.isAdmin ??
          obj.admin ??
          (Array.isArray(obj.roles) && obj.roles.includes("ADMIN")),
        );
      }
      state.isAdmin = derivedIsAdmin;
    });
    builder.addCase(fetchAdminProfile.rejected, (state, action) => {
      console.log("Jelppki admin profile fetch rejected");
      state.loading = false;
      state.error = action.payload as string;
      state.isAdmin = false;
    });
  },
});

export default slice.reducer;
